/**
 * The Item renderer registry.
 *
 * One entry per `item_type`, and the type of the map is what enforces it: `Record<ItemType, …>` is
 * exhaustive, so adding a type to `@pius/contracts` breaks compilation here until an editor exists.
 * That is the same guarantee the backend gets when it validates a submitted value against the type
 * — one union, both sides, no silent fallthrough (docs/engineering/09-frontend.md).
 *
 * There is deliberately **no `default` case and no generic fallback editor.** A fallback would let a
 * new type ship with a text box that quietly writes the wrong value shape.
 */

import type { ItemType } from '@pius/contracts';

import {
  AttachmentRefEditor,
  DateTimeEditor,
  FreeTextEditor,
  MultiOptionEditor,
  MusicRefEditor,
  NumberEditor,
  PersonRefEditor,
  ScriptureRefEditor,
  SingleOptionEditor,
  YesNoEditor,
} from './editors';
import type { ItemEditor } from './types';

export const ITEM_EDITORS: Record<ItemType, ItemEditor> = {
  single_option: SingleOptionEditor,
  multi_option: MultiOptionEditor,
  free_text: FreeTextEditor,
  scripture_ref: ScriptureRefEditor,
  music_ref: MusicRefEditor,
  date_time: DateTimeEditor,
  yes_no: YesNoEditor,
  number: NumberEditor,
  person_ref: PersonRefEditor,
  attachment_ref: AttachmentRefEditor,
};

/**
 * The editor for one Item type.
 *
 * `item_type` arrives over the wire as a string, so an unrecognised one is a real possibility — an
 * older client against a newer server. It renders a plain statement rather than throwing: a
 * workspace with one unfamiliar Item should still be usable for every other Item on it.
 */
export function editorFor(itemType: string): ItemEditor | undefined {
  return Object.prototype.hasOwnProperty.call(ITEM_EDITORS, itemType)
    ? ITEM_EDITORS[itemType as ItemType]
    : undefined;
}

export { summarize } from './summarize';
export { attributionFor, type ItemEditor, type ItemEditorProps } from './types';
